import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../api";
import { ShieldCheck, RefreshCw, Filter, User, Clock } from "lucide-react";

const ACTION_COLORS = {
  "application.status_changed": "#0040A8",
  "application.note_added":     "#9933FF",
  "application.scorecard_set":  "#0099FF",
};
function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/admin/audit-log", { params: { limit: 500 } });
      setRows(r.data?.items || []);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? rows.filter((r) =>
        [r.actor_email, r.action, r.entity_id].some((v) =>
          (v || "").toLowerCase().includes(q.toLowerCase())))
    : rows;

  return (
    <div className="space-y-5" data-testid="admin-audit-log">
      <div className="rounded-sm p-6 text-white bg-brand-gradient flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="eyebrow on-grad-muted">Compliance</span>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1 text-white">Audit log</h2>
          <p className="on-grad-muted text-sm mt-2 max-w-xl">
            Every status change, note, scorecard, and admin action is recorded here with the actor & timestamp.
          </p>
        </div>
        <button onClick={load} className="btn-ghost-dark" data-testid="audit-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="card card-pad flex items-center gap-2 flex-wrap">
        <Filter size={16} className="text-signal" />
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Filter by actor email, action, or entity id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="audit-filter"
        />
        <div className="text-xs text-muted">{filtered.length} of {rows.length}</div>
      </div>

      {err && <div className="card card-pad text-red-600">{err}</div>}
      {loading && <div className="card card-pad animate-pulse text-muted">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="card card-pad text-muted text-center">No audit entries.</div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((r) => {
            const color = ACTION_COLORS[r.action] || "#54607A";
            return (
              <div key={r.id} className="card card-pad flex items-start gap-3" data-testid={`audit-row-${r.id}`}>
                <div className="w-1.5 h-full self-stretch rounded-full" style={{ background: color, minHeight: 40 }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white"
                      style={{ background: color }}>
                      {r.action}
                    </span>
                    <span className="text-xs text-muted inline-flex items-center gap-1">
                      <Clock size={11} />{timeAgo(r.created_at)}
                    </span>
                  </div>
                  <div className="text-sm mt-1.5 inline-flex items-center gap-2 flex-wrap">
                    <User size={12} className="text-muted" />
                    <b>{r.actor_email || r.actor_id}</b>
                    <span className="text-xs text-muted">({r.actor_role})</span>
                    <span className="text-muted">→</span>
                    <span className="text-xs font-mono text-muted">{r.entity_type}#{r.entity_id?.slice(0, 8)}</span>
                  </div>
                  {(r.before || r.after) && (
                    <div className="mt-1.5 text-xs text-muted font-mono inline-flex items-center gap-2 flex-wrap">
                      {r.before && <span>before: <code className="bg-paper px-1 rounded">{JSON.stringify(r.before)}</code></span>}
                      {r.after  && <span>after: <code className="bg-paper px-1 rounded">{JSON.stringify(r.after)}</code></span>}
                    </div>
                  )}
                  {r.meta?.reason && (
                    <div className="text-xs italic text-muted mt-1">“{r.meta.reason}”</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
